import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api-client.js';
import { Badge } from '../components/ui/badge.js';
import { Card, CardBody, CardHeader, CardTitle } from '../components/ui/card.js';

interface Health {
  status: string;
  timestamp: string;
  service: string;
}

type EventStatus = 'pending' | 'awaiting' | 'confirmed' | 'failed';
type CampaignStatus = 'pending' | 'in_progress' | 'completed' | 'failed';

interface EventRow {
  id: string;
  title: string;
  start_at: string;
  status: EventStatus;
  delivery_status: Record<string, string>;
}

interface CampaignRow {
  id: string;
  tag: string;
  start_at: string;
  status: CampaignStatus;
  delivery_status: Record<string, string>;
}

interface ListResp<T> {
  data: T[];
  pagination: { has_more: boolean; next_cursor: string | null };
}

interface DashboardSummary {
  pending_events: number;
  sent_today: number;
  failures_24h: number;
  webhook_backlog: number;
  email_30d?: {
    sent: number;
    opened: number;
    clicked: number;
    replied: number;
    bounced: number;
    failed: number;
  };
  generated_at: string;
}

interface RecentEvent {
  id: string;
  title: string;
  attendee_phone: string;
  start_at: string;
  status: EventStatus | 'pending_approval';
  delivery_status: Record<string, string>;
  updated_at: string;
}

export function DashboardPage(): JSX.Element {
  // Compute the day window once per render. The dashboard intentionally
  // refetches on focus so it stays fresh during operations work.
  const { dayStart, dayEnd } = useMemo(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    return { dayStart: start.toISOString(), dayEnd: end.toISOString() };
  }, []);

  const healthQ = useQuery({
    queryKey: ['health'],
    queryFn: () => api.get('/health') as Promise<Health>,
    refetchInterval: 10_000,
    refetchOnWindowFocus: false,
  });

  // Phase 4b — operator summary tiles. Cookie-or-bearer admin endpoint.
  // 30s refetch is the operations sweet spot: visible enough during an
  // outage, light enough to not hammer the DB.
  const summaryQ = useQuery({
    queryKey: ['admin', 'dashboard', 'summary'],
    queryFn: () => api.get('/v1/admin/dashboard/summary') as Promise<DashboardSummary>,
    refetchInterval: 30_000,
  });

  const recentQ = useQuery({
    queryKey: ['admin', 'dashboard', 'recent-events'],
    queryFn: () =>
      api.get('/v1/admin/dashboard/recent-events?limit=20') as Promise<{ data: RecentEvent[] }>,
    refetchInterval: 30_000,
  });

  const eventsTodayQ = useQuery({
    queryKey: ['events', 'today', dayStart, dayEnd],
    queryFn: () =>
      api.get(
        `/v1/events?from=${encodeURIComponent(dayStart)}&to=${encodeURIComponent(dayEnd)}&limit=100`,
      ) as Promise<ListResp<EventRow>>,
  });

  const campaignsRecentQ = useQuery({
    queryKey: ['campaigns', 'dashboard'],
    queryFn: () => api.get('/v1/campaigns?limit=20') as Promise<ListResp<CampaignRow>>,
  });

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">Dashboard</h1>
        <p className="text-sm text-ink-muted mt-1">
          {new Date().toLocaleDateString(undefined, {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}
        </p>
      </header>

      <SummaryTiles query={summaryQ} />

      <EmailTrackingTile query={summaryQ} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <EventsTodayCard query={eventsTodayQ} />
        <CampaignsCard query={campaignsRecentQ} />
      </div>

      <RecentEventsCard query={eventsTodayQ} />

      <RecentActivityCard query={recentQ} />

      <Card>
        <CardHeader>
          <CardTitle>System</CardTitle>
        </CardHeader>
        <CardBody>
          {healthQ.isLoading && <p className="text-sm text-ink-muted">Checking…</p>}
          {healthQ.error && (
            <p className="text-sm text-danger">
              Failed to reach the App: {(healthQ.error as Error).message}
            </p>
          )}
          {healthQ.data && (
            <dl className="grid grid-cols-3 gap-4 text-sm">
              <Stat
                label="Status"
                value={
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-success" />
                    <span className="font-mono">{healthQ.data.status}</span>
                  </span>
                }
              />
              <Stat label="Service" value={<span className="font-mono">{healthQ.data.service}</span>} />
              <Stat
                label="Last check"
                value={<span className="font-mono">{new Date(healthQ.data.timestamp).toLocaleTimeString()}</span>}
              />
            </dl>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

// ============================================================================
// Per-card components
// ============================================================================

function SummaryTiles({
  query,
}: {
  query: { data?: DashboardSummary; isLoading: boolean; error: unknown };
}): JSX.Element {
  if (query.error) {
    return (
      <Card>
        <CardBody>
          <p className="text-sm text-danger">
            Could not load operator summary: {(query.error as Error).message}
          </p>
        </CardBody>
      </Card>
    );
  }
  const s = query.data;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <Tile label="Pending events" value={s?.pending_events} loading={query.isLoading} tone="info" />
      <Tile label="Sent today" value={s?.sent_today} loading={query.isLoading} tone="success" />
      <Tile label="Failures (24h)" value={s?.failures_24h} loading={query.isLoading} tone="danger" />
      <Tile label="Webhook backlog" value={s?.webhook_backlog} loading={query.isLoading} tone="warning" />
    </div>
  );
}

function Tile({
  label,
  value,
  loading,
  tone,
}: {
  label: string;
  value: number | undefined;
  loading: boolean;
  tone: 'neutral' | 'info' | 'success' | 'warning' | 'danger';
}): JSX.Element {
  const TONE: Record<string, string> = {
    neutral: 'text-ink',
    info: 'text-info',
    success: 'text-success',
    warning: 'text-warning',
    danger: 'text-danger',
  };
  return (
    <Card>
      <CardBody>
        <p className="text-xs text-ink-muted uppercase tracking-wide">{label}</p>
        <p className={`text-3xl font-semibold tabular-nums mt-1 ${TONE[tone]}`}>
          {loading ? '—' : value ?? 0}
        </p>
      </CardBody>
    </Card>
  );
}

/**
 * Phase 5a.5 — email tracking aggregates for the trailing 30 days.
 * Shows sent / opened / clicked / replied with derived rates. Hidden
 * entirely when no email has been sent in the window so first-time
 * tenants aren't distracted by an empty card.
 */
function EmailTrackingTile({
  query,
}: {
  query: { data?: DashboardSummary; isLoading: boolean; error: unknown };
}): JSX.Element | null {
  const stats = query.data?.email_30d;
  if (!stats || stats.sent === 0) return null;
  const pct = (num: number): string =>
    stats.sent > 0 ? `${Math.round((num / stats.sent) * 100)}%` : '—';
  return (
    <Card>
      <CardHeader>
        <CardTitle>Email tracking — last 30 days</CardTitle>
      </CardHeader>
      <CardBody>
        <dl className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
          <BigStat label="Sent" value={stats.sent} />
          <BigStat label="Opened" value={stats.opened} sub={pct(stats.opened)} />
          <BigStat label="Clicked" value={stats.clicked} sub={pct(stats.clicked)} />
          <BigStat label="Replied" value={stats.replied} sub={pct(stats.replied)} />
          <BigStat
            label="Bounced / failed"
            value={stats.bounced + stats.failed}
            sub={pct(stats.bounced + stats.failed)}
          />
        </dl>
      </CardBody>
    </Card>
  );
}

function RecentActivityCard({
  query,
}: {
  query: { data?: { data: RecentEvent[] }; isLoading: boolean; error: unknown };
}): JSX.Element {
  const rows = query.data?.data ?? [];
  return (
    <Card>
      <CardHeader>
        <CardTitle>Recent activity</CardTitle>
        <Link to="/events" className="text-xs text-ink-muted hover:text-ink">All events →</Link>
      </CardHeader>
      <CardBody className="p-0">
        {query.isLoading && <p className="px-4 py-3 text-sm text-ink-muted">Loading…</p>}
        {!!query.error && (
          <p className="px-4 py-3 text-sm text-danger">
            {(query.error as Error).message}
          </p>
        )}
        {!query.isLoading && rows.length === 0 && (
          <p className="px-4 py-3 text-sm text-ink-muted">No recent events.</p>
        )}
        {rows.length > 0 && (
          <ul className="divide-y divide-border">
            {rows.map((e) => (
              <li key={e.id}>
                <Link
                  to={`/events/${e.id}`}
                  className="grid grid-cols-[auto_1fr_auto_auto] items-center gap-3 px-4 py-2 text-sm hover:bg-surface-alt transition"
                >
                  <span className="font-mono text-xs text-ink-muted w-32 truncate">
                    {new Date(e.updated_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                  </span>
                  <span className="truncate">{e.title}</span>
                  <span className="font-mono text-xs text-ink-muted">{e.attendee_phone}</span>
                  <Badge
                    tone={
                      e.status === 'failed' ? 'danger'
                      : e.status === 'confirmed' ? 'success'
                      : e.status === 'pending_approval' ? 'warning'
                      : 'neutral'
                    }
                  >
                    {e.status}
                  </Badge>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

function EventsTodayCard({
  query,
}: {
  query: { data?: ListResp<EventRow>; isLoading: boolean; error: unknown };
}): JSX.Element {
  const events = query.data?.data ?? [];
  const counts = useMemo(() => tally(events.map((e) => e.status)), [events]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Events today</CardTitle>
        <Link to="/calendar" className="text-xs text-ink-muted hover:text-ink">
          Calendar →
        </Link>
      </CardHeader>
      <CardBody>
        {query.isLoading && <p className="text-sm text-ink-muted">Loading…</p>}
        {!!query.error && <p className="text-sm text-danger">Failed to load</p>}
        {query.data && (
          <div className="grid grid-cols-4 gap-2 text-center">
            <BigStat label="Total" value={events.length} tone="neutral" />
            <BigStat label="Awaiting" value={counts.awaiting ?? 0} tone="warning" />
            <BigStat label="Confirmed" value={counts.confirmed ?? 0} tone="success" />
            <BigStat label="Failed" value={counts.failed ?? 0} tone="danger" />
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function CampaignsCard({
  query,
}: {
  query: { data?: ListResp<CampaignRow>; isLoading: boolean; error: unknown };
}): JSX.Element {
  const campaigns = query.data?.data ?? [];
  const counts = useMemo(() => tally(campaigns.map((c) => c.status)), [campaigns]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Campaigns (recent)</CardTitle>
        <Link to="/campaigns" className="text-xs text-ink-muted hover:text-ink">
          All →
        </Link>
      </CardHeader>
      <CardBody>
        {query.isLoading && <p className="text-sm text-ink-muted">Loading…</p>}
        {!!query.error && <p className="text-sm text-danger">Failed to load</p>}
        {query.data && (
          <div className="grid grid-cols-4 gap-2 text-center">
            <BigStat label="Pending" value={counts.pending ?? 0} tone="neutral" />
            <BigStat label="In progress" value={counts.in_progress ?? 0} tone="info" />
            <BigStat label="Completed" value={counts.completed ?? 0} tone="success" />
            <BigStat label="Failed" value={counts.failed ?? 0} tone="danger" />
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function RecentEventsCard({
  query,
}: {
  query: { data?: ListResp<EventRow>; isLoading: boolean; error: unknown };
}): JSX.Element {
  const events = useMemo(() => {
    const list = (query.data?.data ?? []).slice();
    list.sort((a, b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime());
    return list.slice(0, 8);
  }, [query.data]);

  if (events.length === 0 && !query.isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Today's schedule</CardTitle>
        </CardHeader>
        <CardBody>
          <p className="text-sm text-ink-muted">Nothing scheduled today.</p>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Today's schedule</CardTitle>
        <Link to="/events" className="text-xs text-ink-muted hover:text-ink">All events →</Link>
      </CardHeader>
      <CardBody className="p-0">
        <ul className="divide-y divide-border">
          {events.map((e) => (
            <li key={e.id}>
              <Link
                to={`/events/${e.id}`}
                className="flex items-center gap-4 px-4 py-3 hover:bg-surface-alt transition"
              >
                <span className="text-sm font-mono text-ink-muted w-20 flex-shrink-0">
                  {new Date(e.start_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                </span>
                <span className="flex-1 truncate font-medium">{e.title}</span>
                <DeliveryDots delivery={e.delivery_status} />
                <Badge tone={EVENT_TONE[e.status]}>{e.status}</Badge>
              </Link>
            </li>
          ))}
        </ul>
      </CardBody>
    </Card>
  );
}

const EVENT_TONE: Record<EventStatus, 'neutral' | 'success' | 'warning' | 'danger'> = {
  pending: 'neutral',
  awaiting: 'warning',
  confirmed: 'success',
  failed: 'danger',
};

function DeliveryDots({ delivery }: { delivery: Record<string, string> }): JSX.Element {
  const channels: ('voice' | 'sms' | 'email')[] = ['voice', 'sms', 'email'];
  const present = channels.filter((c) => c in delivery);
  if (present.length === 0) return <span className="text-xs text-ink-muted">—</span>;
  return (
    <div className="flex gap-1">
      {present.map((c) => {
        const status = delivery[c];
        const dot =
          status === 'completed' ? 'bg-success'
          : status === 'failed' ? 'bg-danger'
          : status === 'sent' ? 'bg-info'
          : 'bg-warning';
        return (
          <span key={c} title={`${c}: ${status}`} className={`w-2 h-2 rounded-full ${dot}`} />
        );
      })}
    </div>
  );
}

// ============================================================================
// Building blocks
// ============================================================================

function Stat({ label, value }: { label: string; value: React.ReactNode }): JSX.Element {
  return (
    <div>
      <dt className="text-ink-muted">{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function BigStat({
  label,
  value,
  tone = 'neutral',
  sub,
}: {
  label: string;
  value: number;
  tone?: 'neutral' | 'info' | 'success' | 'warning' | 'danger';
  sub?: string;
}): JSX.Element {
  const TONE: Record<string, string> = {
    neutral: 'text-ink',
    info: 'text-info',
    success: 'text-success',
    warning: 'text-warning',
    danger: 'text-danger',
  };
  return (
    <div className="rounded border border-border bg-surface-alt py-3 px-2 text-center">
      <div className={`text-2xl font-semibold ${TONE[tone]}`}>{value}</div>
      {sub && (
        <div className="text-[11px] tabular-nums text-ink-muted">{sub}</div>
      )}
      <div className="text-[11px] uppercase tracking-wide text-ink-muted mt-0.5">{label}</div>
    </div>
  );
}

function tally<T extends string>(values: T[]): Partial<Record<T, number>> {
  const out: Partial<Record<T, number>> = {};
  for (const v of values) out[v] = (out[v] ?? 0) + 1;
  return out;
}
